import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PublicCard } from '../lib/types';

export interface CartLine {
  assignmentId: string;
  productId: string;
  name: string;
  barcodeNo: string;
  photoId: string | null;
  price: string | null;
  image: string | null;
  rackNumber: string;
  quantity: number;
  maxQuantity: number | null;
}

interface CartState {
  /** cart is scoped to one rack/expo session */
  qrToken: string | null;
  rackNumber: string | null;
  lines: CartLine[];
  setContext: (qrToken: string, rackNumber: string) => void;
  add: (card: PublicCard, rackNumber: string, qty?: number) => void;
  setQty: (assignmentId: string, qty: number) => void;
  remove: (assignmentId: string) => void;
  clear: () => void;
  count: () => number;
  total: () => number;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      qrToken: null,
      rackNumber: null,
      lines: [],

      setContext(qrToken, rackNumber) {
        // switching to a different rack keeps the cart (multi-rack booking allowed)
        set({ qrToken, rackNumber });
      },

      add(card, rackNumber, qty = 1) {
        const lines = [...get().lines];
        const existing = lines.find((l) => l.assignmentId === card.assignmentId);
        const cap = card.availableQuantity;
        if (existing) {
          existing.quantity = Math.min(existing.quantity + qty, cap ?? 99);
        } else {
          lines.push({
            assignmentId: card.assignmentId,
            productId: card.productId,
            name: card.name,
            barcodeNo: card.barcodeNo,
            photoId: card.photoId,
            price: card.price,
            image: card.image?.thumbnailUrl ?? card.image?.url ?? null,
            rackNumber,
            quantity: Math.min(qty, cap ?? 99),
            maxQuantity: cap,
          });
        }
        set({ lines });
      },

      setQty(assignmentId, qty) {
        const lines = get()
          .lines.map((l) =>
            l.assignmentId === assignmentId
              ? { ...l, quantity: Math.max(1, Math.min(qty, l.maxQuantity ?? 99)) }
              : l,
          )
          .filter((l) => l.quantity > 0);
        set({ lines });
      },

      remove(assignmentId) {
        set({ lines: get().lines.filter((l) => l.assignmentId !== assignmentId) });
      },

      clear() {
        set({ lines: [] });
      },

      count() {
        return get().lines.reduce((n, l) => n + l.quantity, 0);
      },

      total() {
        return get().lines.reduce((n, l) => n + Number(l.price ?? 0) * l.quantity, 0);
      },
    }),
    { name: 'jx_cart' },
  ),
);
